/**
 * Lista de clases de licencia del municipio (nomenclador).
 *
 * La fuente de verdad es el backend: GET /api/clases?todas=1 (admin y operador, solo lectura) devuelve
 * las clases activas y también las inactivas (cada una con `activo`), para poder rotular las clases que
 * una persona ya tiene aunque hoy no se puedan elegir. La lista la configura solo el superadmin.
 *
 * Si el pedido falla o viene vacío se usa la lista fija de siempre (CLASES_POR_DEFECTO, sin descripciones):
 * el formulario nunca se queda sin clases para elegir.
 *
 * La lista se pide una sola vez por sesión (por token) y se comparte entre el formulario, el listado, la
 * vista previa y la impresión del carnet: así los cuatro muestran exactamente lo mismo.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Lista fija histórica (la que tenía PadronFormModal.jsx). Mismo orden.
export const CLASES_POR_DEFECTO = [
  'A1.1', 'A1.2', 'A1.4', 'A2.1', 'A2.2', 'A3',
  'B1', 'B2',
  'C',
  'D1', 'D2', 'D3', 'D4',
  'E1', 'E2',
  'F',
  'G1', 'G2', 'G3',
];

const construirCatalogo = (clases, { porDefecto, ok }) => {
  const lista = clases.map((c, i) => ({
    codigo: c.codigo,
    descripcion: c.descripcion || null,
    orden: c.orden ?? i + 1,
    activo: c.activo !== false,
  }));
  const porCodigo = {};
  lista.forEach((c) => { porCodigo[c.codigo] = c; });
  return { clases: lista, activas: lista.filter((c) => c.activo), porCodigo, porDefecto, ok };
};

const catalogoFijo = () => construirCatalogo(
  CLASES_POR_DEFECTO.map((codigo, i) => ({ codigo, descripcion: null, orden: i + 1, activo: true })),
  { porDefecto: true, ok: false }
);

// Catálogo ya resuelto para el token actual (lectura síncrona) y el pedido en curso, si lo hay.
let cache = { token: null, catalogo: null, pendiente: null };

const tokenActual = () => {
  try { return sessionStorage.getItem('token'); } catch { return null; }
};

/** Catálogo ya cargado para la sesión actual, o null si todavía no se pidió (o cambió la sesión). */
export const catalogoClasesCargado = () => {
  const token = tokenActual();
  return cache.token === token && cache.catalogo ? cache.catalogo : null;
};

/** Pide la lista (una sola vez por sesión). Nunca rechaza: ante un error o una lista vacía devuelve la fija. */
export const cargarClasesMunicipio = () => {
  const token = tokenActual();
  if (cache.token !== token) cache = { token, catalogo: null, pendiente: null };
  if (cache.catalogo) return Promise.resolve(cache.catalogo);
  if (cache.pendiente) return cache.pendiente;

  const pedido = axios
    .get(`${API_URL}/clases`, { params: { todas: 1 }, headers: { Authorization: `Bearer ${token}` } })
    .then(({ data }) => {
      const clases = Array.isArray(data?.clases) ? data.clases.filter((c) => c && typeof c.codigo === 'string') : [];
      if (clases.length === 0) return null;
      return construirCatalogo(clases, { porDefecto: !!data.por_defecto, ok: true });
    })
    .catch((err) => {
      console.error('No se pudo cargar la lista de clases, se usa la lista fija:', err);
      return null;
    })
    .then((catalogo) => {
      if (cache.token !== token) return catalogo || catalogoFijo();
      if (catalogo) cache.catalogo = catalogo;
      cache.pendiente = null; // un fallo no se cachea: el próximo pedido vuelve a intentar
      return catalogo || catalogoFijo();
    });
  cache.pendiente = pedido;
  return pedido;
};

/** Olvida lo cargado (por ejemplo, al cerrar sesión). */
export const invalidarClasesMunicipio = () => { cache = { token: null, catalogo: null, pendiente: null }; };

/**
 * Hook: { catalogo, cargando }. `catalogo` siempre existe (mientras carga, o si falla, es la lista fija);
 * `catalogo.ok` es true cuando salió del backend.
 */
export const useClasesMunicipio = () => {
  const [catalogo, setCatalogo] = useState(() => catalogoClasesCargado());
  useEffect(() => {
    let vivo = true;
    if (!catalogoClasesCargado()) {
      cargarClasesMunicipio().then((c) => { if (vivo) setCatalogo(c); });
    }
    return () => { vivo = false; };
  }, []);
  return { catalogo: catalogo || catalogoFijo(), cargando: !catalogo };
};

/** Texto del tooltip de una clase: "CÓDIGO — descripción completa" (o solo el código). */
export const tituloClase = (catalogo, codigo) => {
  const d = catalogo?.porCodigo?.[codigo]?.descripcion;
  return d ? `${codigo} — ${d}` : codigo;
};

/**
 * Clases del contribuyente de EJEMPLO del Diseñador y de las miniaturas: el PEOR CASO del municipio, o sea las 3
 * clases activas con la descripción más larga (código + descripción) juntas. Si el texto entra con ellas, entra
 * con cualquier combinación de 3 clases. Sin lista del backend (o sin descripciones), las dos de siempre (B1, B2).
 */
export const clasesDeEjemplo = (catalogo) => {
  if (!catalogo || !catalogo.ok || catalogo.activas.length === 0) return ['B1', 'B2'];
  const conDescripcion = catalogo.activas.filter((c) => c.descripcion);
  if (conDescripcion.length === 0) return catalogo.activas.slice(0, 3).map((c) => c.codigo);
  return [...conDescripcion]
    .sort((a, b) => `${b.codigo} ${b.descripcion}`.length - `${a.codigo} ${a.descripcion}`.length)
    .slice(0, 3)
    .map((c) => c.codigo);
};
